import { Box } from "@gemini/core";
import { Button } from "@gemini/ui";
import { CloseIcon } from "@gemini/icons";
import { useEffect, useEffectEvent } from "react";
import { useFeatureFlagging } from "@aviv-trak/feature-flagging";
import { useBrand } from "@aviv-unified-frontend/runtime/brand";

import { BRAND_CONFIGURATION } from "../../../../../configs/brands";
import { STATIC_ASSETS_PREFIX } from "../../../../constants/assets";
import { useTranslate } from "../../../../utils/useTranslate";
import { useCustomBannerTracking } from "../useCustomBannerTracking";

import type { CustomBannerProps } from "./types";

const { aspectRatio } = BRAND_CONFIGURATION.appBanner?.custom?.bottom ?? {};

export const BottomCustomBanner = ({
  bannerInfo,
  onClick,
  onDismiss,
  isBannerVisible,
}: CustomBannerProps) => {
  const t = useTranslate();
  const { id: brandId } = useBrand();
  const { getFeatureFlag } = useFeatureFlagging();

  const featureFlagActivated =
    getFeatureFlag("ufrn_custom_banner_bottom_layout") === "on";

  const { translation, storeUrl, NewLayoutStoreUrl } = bannerInfo;
  const isNewLayout = featureFlagActivated && Boolean(NewLayoutStoreUrl);
  const effectiveStoreUrl = isNewLayout ? NewLayoutStoreUrl : storeUrl;

  const { onDisplayEvent, onClickEvent, onDismissEvent } =
    useCustomBannerTracking(isNewLayout ? "b" : "a");

  const onDisplayEventStable = useEffectEvent(onDisplayEvent);

  useEffect(() => {
    if (!isBannerVisible) return;

    onDisplayEventStable();
  }, [isBannerVisible]);

  const title = t(`customAppBanner.${translation}.mobile.title`);
  const description = t(`customAppBanner.${translation}.mobile.description`);

  return (
    <Box
      /**
       * This custom attribute is used by React Aria to identify
       * top layers in the application (like modals, banners, etc.).
       *
       * See https://github.com/adobe/react-spectrum/issues/8784#issuecomment-3415368636
       */
      data-react-aria-top-layer="true"
      position="fixed"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      overflow="hidden"
      gap="spacing.24"
      bottom="spacing.none"
      right="spacing.none"
      zIndex="zIndex.4"
      width="100%"
      height="fit-content"
      pointerEvents="all"
      backgroundColor="color.background.light"
      borderTopLeftRadius="radius.16"
      borderTopRightRadius="radius.16"
      paddingTop={isNewLayout ? "spacing.none" : "spacing.40"}
      paddingBottom="spacing.40"
      boxShadow="shadow.32"
      color="color.content.active.default"
      transitionDuration="motionDuration.300"
      transitionTimingFunction="motionEasing.ease-out"
      transitionProperty="transform"
      transform={
        isBannerVisible
          ? "translate3d(0, 0, 0)"
          : "translate3d(0, calc(100% + 2rem), 0)"
      }
    >
      {/* Close Button */}
      <Button
        position="absolute"
        top={isNewLayout ? "spacing.8" : "spacing.4"}
        right={isNewLayout ? "spacing.8" : "spacing.4"}
        size={isNewLayout ? "40" : "32"}
        icon={CloseIcon}
        iconOnly
        onPress={() => {
          onDismiss();
          onDismissEvent();
        }}
        type="button"
        variant={isNewLayout ? "secondary-floating" : "tertiary"}
        zIndex="zIndex.1"
      >
        Close
      </Button>
      {isNewLayout ? (
        <Box
          as="img"
          unsafe_style={{ aspectRatio }}
          src={`${STATIC_ASSETS_PREFIX}/custom-banner/bottom/illustration@1.webp`}
          srcSet={`${STATIC_ASSETS_PREFIX}/custom-banner/bottom/illustration@1.webp, ${STATIC_ASSETS_PREFIX}/custom-banner/bottom/illustration@2.webp 2x`}
          alt={t(`customAppBanner.alt.illustration`)}
          {...(brandId === "sl" && {
            backgroundColor: "color.surface.brand.primary.hover",
          })}
          width="100%"
          height="auto"
          objectFit="cover"
        />
      ) : (
        <Box
          as="img"
          src={`${STATIC_ASSETS_PREFIX}/custom-banner/logo-brand.svg`}
          alt={t(`customAppBanner.alt.logo`)}
          alignSelf="center"
        />
      )}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
        paddingLeft="spacing.16"
        paddingRight="spacing.16"
      >
        <Box
          textAlign="center"
          paddingBottom="spacing.6"
          typography="typography.headline.20.bold"
        >
          {title}
        </Box>
        <Box
          textAlign="center"
          paddingBottom="spacing.24"
          typography="typography.body.14.regular"
        >
          {description}
        </Box>
        <Button
          onPress={() => {
            window.open(effectiveStoreUrl, "_blank", "noopener,noreferrer");
            onClick();
            onClickEvent("CTA");
          }}
        >
          {t(`customAppBanner.openTheApp`)}
        </Button>
      </Box>
    </Box>
  );
};
